import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { MapPinIcon, UsersIcon } from 'lucide-react';
import { useMemo } from 'react';

import type { AccountDTO, LocationDTO } from '@invenco/accounts-interface';

import {
  DataTable,
  DataTableFilterMenu,
  DataTablePageContent,
  DataTablePagination,
  DataTableRefreshMenu,
  DataTableToolbar,
  useDataTable,
  useDataTableState,
  useRowNavigationCallback,
} from '@/components/data-table';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages, formatDateTime } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { locationStatusType } from '@/shared/status-maps';
import { locationStatusTitle, locationTypeTitle } from '@/shared/title-maps';

const columnHelper = createColumnHelper<LocationDTO>();

const columns = [
  columnHelper.accessor('name', { header: 'Name' }),
  columnHelper.accessor('type', {
    header: 'Type',
    cell: ({ getValue }) => locationTypeTitle[getValue()],
  }),
  columnHelper.accessor('accountName', { header: 'Account' }),
  columnHelper.accessor('warehouseLocationName', { header: 'Warehouse' }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return <StatusBadge status={status} type={locationStatusType} title={locationStatusTitle} />;
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    cell: ({ getValue }) => formatDateTime(getValue()),
  }),
];

export function LocationList() {
  const { accountsGateway } = useGateways();
  const onClickRow = useRowNavigationCallback<LocationDTO>('/locations');

  const { data: accountsData } = useQuery({
    queryKey: ['accounts'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getAccounts({ cursor, take }, { signal }),
      ),
  });

  const accountsById = useMemo<Record<string, AccountDTO>>(
    () =>
      Object.fromEntries(
        accountsData?.flatMap((account) => (account.id ? [[account.id, account] as const] : [])) ??
          [],
      ),
    [accountsData],
  );

  const { state, filters, onPaginationChange, onSortingChange } = useDataTableState({
    columns,
    filterDefs: useMemo(
      () => ({
        accountId: {
          variant: 'multiselect',
          label: 'Account',
          icon: UsersIcon,
          options: accountsData?.map((account) => account.id) ?? [],
          optionLabel: (accountId: string) => accountsById[accountId]?.name ?? accountId,
        },
      }),
      [accountsById, accountsData],
    ),
  });

  const { data, isLoading, isFetching, isPlaceholderData, isError, refetch } = useQueryWithInput({
    parentKey: 'locations',
    input: { search: filters.search },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getLocations({ search: input.search, cursor, take }, { signal }),
      ),
    placeholderData: keepPreviousData,
  });

  // Apply client-side account filter
  const filteredData = useMemo(() => {
    if (!data) return data;

    const accountFilter = filters.filterState.find((f) => f.id === 'accountId');
    const accountIds = accountFilter?.value as string[] | undefined;

    if (!accountIds || accountIds.length === 0) {
      return data;
    }

    return data.filter((location) => accountIds.includes(location.accountId));
  }, [data, filters.filterState]);

  const table = useDataTable({
    data: filteredData,
    columns,
    isLoading,
    isLoadingWithStaleData: isFetching && isPlaceholderData,
    hasError: isError,
    manualPagination: false,
    manualSorting: false,
    autoResetPageIndex: false,
    onPaginationChange,
    onSortingChange,
    state,
  });

  return (
    <Page>
      <Header>
        <HeaderIcon>
          <MapPinIcon />
        </HeaderIcon>
        <HeaderTitle>Locations</HeaderTitle>
      </Header>
      <DataTablePageContent>
        <DataTableToolbar>
          <DataTableFilterMenu filters={filters} />
          <DataTableRefreshMenu onRefresh={refetch} />
        </DataTableToolbar>
        <DataTable table={table} onClickRow={onClickRow} />
        <DataTablePagination table={table} />
      </DataTablePageContent>
    </Page>
  );
}
